import React, { useEffect, useRef, useState } from "react";
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import $ from 'jquery';
import Swal from 'sweetalert2';
import * as action from '../Action/Tags/Tags_Action';
import Model from "./Model/Index";
import NumberDisplay from "./NumberDisplay/Index";
import { usePermission } from "../hooks/usePermission";
import { PERM } from "../config/permissions";
import { auditDataTableColumns, AuditTrailStyles } from "./AuditTrail";
import PriorityGenres, { MAX_PRIORITY_GENRES } from "./PriorityGenres";

/** `priority` arrives as 0/1 from the API, but tolerate a boolean too. */
const isPriority = (row) => row?.priority === true || Number(row?.priority) > 0;

/** Tab strip + the Priority column/toggle styling for the All Genres table. */
const GenreTabStyles = () => (
    <style>{`
        .genre-tabs {
            display: inline-flex; gap: 6px; margin: 0;
            background: var(--glass-bg); border: 1px solid var(--border-color);
            border-radius: var(--radius-sm); padding: 4px;
        }
        .genre-tab-btn {
            border: none; background: transparent; color: var(--text-color);
            font-size: 14px; font-weight: 500; padding: 7px 18px;
            border-radius: 6px; transition: var(--transition-fast);
            cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
        }
        .genre-tab-btn:hover { color: var(--body-text-color); }
        .genre-tab-btn.active { background: var(--first-color); color: #fff; }
        .genre-priority-badge {
            display: inline-flex; align-items: center; gap: 5px;
            font-size: 12px; font-weight: 700; color: var(--first-color);
            background: rgba(var(--first-color-rgb), 0.12);
            border-radius: 12px; padding: 3px 10px; white-space: nowrap;
        }
        .genre-priority-off { color: var(--text-color); font-size: 13px; }
        .genre-priority-toggle {
            background: transparent; border: 1px solid var(--border-color);
            color: var(--text-color);
        }
        .genre-priority-toggle:hover:not(:disabled) {
            border-color: var(--first-color); color: var(--first-color);
        }
        .genre-priority-toggle.active {
            border-color: var(--first-color); color: var(--first-color);
            background: rgba(var(--first-color-rgb), 0.12);
        }
    `}</style>
);

// This screen is labelled "Genres" in the UI and is backed by the tags resource.
const Tags = () => {
    const { canCreate, canEdit, canDelete } = usePermission();
    const allowCreate = canCreate(PERM.GENRES);
    const allowEdit = canEdit(PERM.GENRES);
    const allowDelete = canDelete(PERM.GENRES);
    const dispatch = useDispatch();
    const tableRef = useRef(null);
        const updateTagsLoading = useSelector(state => state.UpdateTagsReducer);
        const addTagsLoading = useSelector(state => state.AddTagsReducer);
    const [showModel, setShowModel] = useState({
        show: false,
        title: 'Add'
    });
    const [count, setCount] = useState(0);
    const [errors, setErrors] = useState({});
    const [tagsData, setTagsData] = useState({
        id: 0,
        name: '',
        priority: 0
    });
    const [activeTab, setActiveTab] = useState('all');
    const [priorityCount, setPriorityCount] = useState(null);
    const dataTableInstanceRef = useRef(null);
    const nameRefFocus = useRef(null);
    const submittingRef = useRef(false);
    // Mirrors the Priority tab's unsaved-reorder state so the tab strip can warn
    // before unmounting it (and losing that pending order).
    const priorityDirtyRef = useRef(false);
    const activeTabRef = useRef(activeTab);
    activeTabRef.current = activeTab;
    // The star-toggle handler is bound once when DataTables initialises, so it
    // has to read the live count through a ref rather than closing over state.
    const priorityCountRef = useRef(priorityCount);
    priorityCountRef.current = priorityCount;

    // Seeds the Priority tab badge without having to mount the tab first.
    useEffect(() => {
        dispatch(action.getTags({ page: 1, limit: 1, filter: 'priority' }))
            .then((res) => {
                const rd = res?.responseDetails || {};
                const total = rd.total ?? rd.totalRecords;
                if (typeof total === 'number') setPriorityCount(total);
            })
            .catch(() => { /* badge just stays hidden */ });
    }, [dispatch]);

    // The table stays mounted for the life of the screen — DataTables re-parents
    // it into its own wrapper, so letting React unmount it on a tab switch makes
    // React's removeChild target a node that is no longer its child. The Priority
    // tab hides it with CSS instead.
    useEffect(() => {
        let tableRefCurrent = tableRef.current;
        const $ = window.$ || window.jQuery;

        let currentSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';

        function setPaginationOptions() {
            if (currentSize === 'mobile') {
                $.fn.dataTable.ext.pager.numbers_length = 0;
                return 'simple';
            } else {
                $.fn.dataTable.ext.pager.numbers_length = 5;
                return 'simple_numbers';
            }
        }

        const initDataTable = () => {
            const pagingType = setPaginationOptions();
    
            if ($ && tableRefCurrent) {
                dataTableInstanceRef.current = $(tableRefCurrent).DataTable({
                    processing: true,
                    serverSide: true,
                    scrollX: true,
                    pagingType: pagingType,
                    ajax: (dataTablesParams, callback) => {
                        const params = {
                            search: dataTablesParams.search.value,
                            page: Math.floor(dataTablesParams.start / dataTablesParams.length) + 1,
                            limit: dataTablesParams.length,
                        };
                        dispatch(action.getTags(params)).then((action) => {
                            const rd = action.responseDetails || {};

                            const rawRows = rd.rows || rd.data || [];
                            const seen = new Map();
                            rawRows.forEach(r => { if (!seen.has(r.id)) seen.set(r.id, r); });
                            const rows = Array.from(seen.values());

                            const total = rd.total || rd.totalRecords || 0;

                            setCount(total);

                            callback({

                                draw: dataTablesParams.draw,

                                data: rows,

                                recordsTotal: total,

                                recordsFiltered: rd.recordsFiltered || total,

                            });
                        }).catch((err) => {
                            console.error('Tags list error:', err);
                            callback({ draw: dataTablesParams.draw, data: [], recordsTotal: 0, recordsFiltered: 0 });
                        });
                    },
                    destroy: true,
                    columns: [
                        { data: 'name', title: "Name" },
                        {
                            data: null,
                            title: 'Priority',
                            orderable: false,
                            render: function (data, type, row) {
                                if (!isPriority(row)) {
                                    return '<span class="genre-priority-off">—</span>';
                                }
                                const rank = row?.priority_rank;
                                return `<span class="genre-priority-badge">
                                    <i class="fas fa-star"></i>${rank != null ? ` #${rank}` : ''}
                                </span>`;
                            }
                        },
                        ...auditDataTableColumns(),
                        {
                            data: null,
                            title: 'Action',
                            orderable: false,
                            render: function (data, type, row) {
                            const on = isPriority(row);
                            return `
                                <div class="d-flex gap-2">
                                    ${allowEdit ? `<button class="btn btn-sm genre-priority-toggle${on ? ' active' : ''}" title="${on ? 'Remove from priority' : 'Add to priority'}">
                                        <i class="${on ? 'fas' : 'far'} fa-star"></i>
                                    </button>` : ''}
                                    ${allowEdit ? `<button class="btn btn-sm btn-primary edit-btn" title="Edit">
                                        <i class="fas fa-edit"></i>
                                    </button>` : ''}
                                    ${allowDelete ? `<button class="btn btn-sm btn-danger delete-btn" title="Delete">
                                        <i class="fas fa-trash-alt"></i>
                                    </button>` : ''}
                                </div>
                            `;
                            }
                        }
                    ]
                });

                $(tableRefCurrent).on('preXhr.dt', function () {
                    const processingEl = $('.dataTables_processing');
                    const visibleCols = $(tableRefCurrent).find('thead th:visible').length;

                    if (processingEl.length) {
                        const loaderRow = `
                            <tr class="dt-temp-loader">
                                <td colspan="${visibleCols}" class="text-center">
                                    <div class="dataTables_processing card" style="display:block;margin:0 auto">${processingEl.html()}</div>
                                </td>
                            </tr>
                        `;
                        $(tableRefCurrent).find('tbody').html(loaderRow);
                    }
                });

                $(tableRefCurrent).on('xhr.dt', function () {
                    $(tableRefCurrent).find('tbody .dt-temp-loader').remove();
                });

                $(tableRefCurrent).off('click', '.edit-btn');
                $(tableRefCurrent).on('click', '.edit-btn', function (e) {
                    let rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    openModel('Edit');
                    setTagsData({
                        id: rowData.id,
                        name: rowData.name,
                        // Carried through the update call so renaming a genre never
                        // silently drops it out of the priority list.
                        priority: isPriority(rowData) ? 1 : 0
                    });
                });

                $(tableRefCurrent).off('click', '.genre-priority-toggle');
                $(tableRefCurrent).on('click', '.genre-priority-toggle', function (e) {
                    if (!allowEdit) {
                        toast.error("Opps! You don't have Permission.");
                        return false;
                    }

                    const rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    const on = isPriority(rowData);

                    // Promoting is the only direction that can breach the cap.
                    // `null` means the count never loaded, so let the click through
                    // rather than blocking on a number we do not have.
                    const current = priorityCountRef.current;
                    if (!on && typeof current === 'number' && current >= MAX_PRIORITY_GENRES) {
                        toast.error(
                            `Only ${MAX_PRIORITY_GENRES} genres can be in the priority list. Remove one from the Priority tab first.`,
                        );
                        return false;
                    }

                    const $btn = $(this);
                    $btn.prop('disabled', true);

                    dispatch(action.updateTags({ id: rowData.id, name: rowData.name, priority: on ? 0 : 1 })).then(() => {
                        toast.success(on
                            ? `"${rowData.name}" removed from priority.`
                            : `"${rowData.name}" added to priority.`);
                        setPriorityCount(prev => (typeof prev === 'number' ? Math.max(prev + (on ? -1 : 1), 0) : prev));
                        if (dataTableInstanceRef.current) {
                            dataTableInstanceRef.current.ajax.reload(null, false);
                        }
                    }).catch((err) => {
                        $btn.prop('disabled', false);
                        toast.error(err?.responseMessage || err?.message || 'Failed to update priority.');
                    });
                });
                
                $(tableRefCurrent).off('click', '.delete-btn');
                $(tableRefCurrent).on('click', '.delete-btn', function (e) {
                    if (!allowDelete) {
                        toast.error("Opps! You don't have Permission.");
                        return false;
                    }

                    let rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    Swal.fire({
                        title: "Are you sure?",
                        text: "You won't be able to revert this!",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "var(--first-color)",
                        cancelButtonColor: "var(--error-color)",
                        confirmButtonText: "Yes, delete it!",
                        customClass: {
                            confirmButton: 'btn',
                            cancelButton: 'btn'
                        }
                    }).then((result) => {
                        if (result.isConfirmed) {
                            dispatch(action.deleteTags({id: rowData.id})).then((action) => {
                                Swal.fire({
                                    title: "Deleted!",
                                    text: "Your data has been deleted.",
                                    icon: "success",
                                    customClass: {
                                        confirmButton: 'btn',
                                    }
                                });
                                if (dataTableInstanceRef.current) {
                                    dataTableInstanceRef.current.ajax.reload(null, false);
                                }
                            }).catch((err) => {
                                Swal.fire({
                                    icon: "error",
                                    title: "Oops...",
                                    text: "Something went wrong!",
                                    customClass: {
                                        confirmButton: 'btn',
                                    }
                                });
                            });
                        }
                    });
                });
            }
        }

        initDataTable();

        const handleResize = () => {
            const newSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';
            if (newSize !== currentSize) {
                currentSize = newSize;

                // Re-initialising while the table is hidden would measure zero
                // column widths; the next visit to the All tab re-inits anyway.
                if (activeTabRef.current !== 'all') return;

                // Destroy and re-init
                if ($.fn.DataTable.isDataTable(tableRefCurrent)) {
                    $(tableRefCurrent).DataTable().destroy();
                }
                initDataTable();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if ( $.fn.DataTable.isDataTable(tableRefCurrent) ) {
                $(tableRefCurrent).DataTable().destroy();
            }
            dataTableInstanceRef.current = null;
        };
    }, [dispatch, allowEdit, allowDelete]);

    // Returning from the Priority tab: priority flags/ranks may have changed, so
    // pull fresh rows. Skipped on first render, when the table has just loaded.
    const prevTabRef = useRef(activeTab);
    useEffect(() => {
        const leftPriorityTab = prevTabRef.current === 'priority' && activeTab === 'all';
        prevTabRef.current = activeTab;

        if (leftPriorityTab && dataTableInstanceRef.current) {
            dataTableInstanceRef.current.ajax.reload(null, false);
            // Widths were measured while the table was hidden if the viewport
            // changed in the meantime; recalculate now that it is visible again.
            try { dataTableInstanceRef.current.columns.adjust(); } catch (e) { /* pre-init */ }
        }
    }, [activeTab]);

    const switchTab = (tab) => {
        if (tab === activeTab) return;

        if (activeTab === 'priority' && priorityDirtyRef.current) {
            Swal.fire({
                title: "Unsaved order",
                html: "You've reordered the priority genres but haven't saved yet.<br/>Leaving this tab will <strong>discard those changes</strong>.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Discard changes",
                cancelButtonText: "Stay here",
                confirmButtonColor: "var(--error-color)",
                cancelButtonColor: "var(--first-color)",
                reverseButtons: true,
                focusCancel: true,
                customClass: {
                    confirmButton: 'btn',
                    cancelButton: 'btn'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    priorityDirtyRef.current = false;
                    setActiveTab(tab);
                }
            });
            return;
        }

        setActiveTab(tab);
    }

    const openModel = (name) => {
        setShowModel({
            show: true,
            title: name
        });
        $("body").addClass("no_scroll");
        $('.overlay').addClass('qv_active');
    }

    const closeModel = (name) => {
        setShowModel({
            show: false,
            title: name
        });
        $("body").removeClass("no_scroll");
        $('.overlay').removeClass('qv_active');
        setErrors({});
        setTagsData({
            id: 0,
            name: '',
            priority: 0
        });
    }
    
    const onChange = (e) => {
        setTagsData({ ...tagsData, [e.target.name]: e.target.value })
    }

    const addTagsData = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        // Covers both Add and Edit — the backend guards both with types:edit.
        if (!allowEdit) {
            toast.error("Opps! You don't have Permission.");
            return false;
        }

        setErrors({});
        let customErrors = {};
        if (tagsData.name === '') {
            customErrors = { ...customErrors, name: "Please Enter name" }
            nameRefFocus.current.focus();
        }

        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return true
        }

        var data = {
            'name': tagsData.name,
        };

        submittingRef.current = true;
        if(tagsData.id !== 0){
            data.id = tagsData.id;
            // Update replaces `priority`, so echo back what the row already had.
            data.priority = tagsData.priority ? 1 : 0;
            dispatch(action.updateTags(data)).then((response) => {
                submittingRef.current = false;
                toast.success(response.responseMessage);
                closeModel('Add');
    
                if (dataTableInstanceRef.current) {
                    dataTableInstanceRef.current.ajax.reload(null, false);
                }
            }).catch(error => {
                submittingRef.current = false;
                toast.error(error?.responseMessage || error?.message || 'An error occurred. Please try again.');
            })
        } else {
            dispatch(action.addTags(data)).then((response) => {
                submittingRef.current = false;
                toast.success(response.responseMessage);
                closeModel('Add');
    
                if (dataTableInstanceRef.current) {
                    dataTableInstanceRef.current.ajax.reload(null, false);
                }
            }).catch(error => {
                submittingRef.current = false;
                toast.error(error?.responseMessage || error?.message || 'An error occurred. Please try again.');
            })
        }
    }

    return (
        <>
            {/* { loading ? <Loader /> :  */}
                <>
                    <AuditTrailStyles />
                    <GenreTabStyles />
                    <div className='overlay'></div>
                    <section id="genres-section" className="genres-section section">
                        <div className="card">
                            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <h4 className="m-0">Genres (<span className="total_user">{<NumberDisplay count={count} />}</span>)</h4>
                                    <div className="genre-tabs">
                                        <button
                                            type="button"
                                            className={`genre-tab-btn${activeTab === 'all' ? ' active' : ''}`}
                                            onClick={() => switchTab('all')}
                                        >
                                            All Genres
                                        </button>
                                        <button
                                            type="button"
                                            className={`genre-tab-btn${activeTab === 'priority' ? ' active' : ''}`}
                                            onClick={() => switchTab('priority')}
                                        >
                                            <i className="fas fa-star me-2"></i>
                                            Priority{priorityCount !== null ? ` (${priorityCount}/${MAX_PRIORITY_GENRES})` : ''}
                                        </button>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {activeTab === 'all' && allowCreate && (
                                        <button className="btn upgrade-btn add-genre-btn" onClick={(e) => openModel('Add')}>
                                            Add Genre
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Never unmounted — see the DataTables effect above. */}
                                <div className={activeTab === 'all' ? '' : 'd-none'}>
                                    <table ref={tableRef} className="genres-table user-table table table-striped w-100 dataTable no-footer" id="genres-Contents" role="grid"></table>
                                </div>
                                {activeTab === 'priority' && (
                                    <PriorityGenres
                                        allowEdit={allowEdit}
                                        onDirtyChange={(isDirty) => { priorityDirtyRef.current = isDirty; }}
                                        onCountChange={setPriorityCount}
                                    />
                                )}
                            </div>
                        </div>
                    </section>

                    {
                        showModel.show && (
                            <Model className="common-popup add-genre-popup active">
                                <div className="edit-heading d-flex align-items-center justify-content-between">
                                    <h3>{showModel.title} Genre</h3>
                                    <button className="common-close edit-close-btn" onClick={(e) => closeModel('Add')}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18">
                                            <path fill="#ffffff"
                                                d="M19.95 16.75l-.05-.4-1.2-1-5.2-4.2c-.1-.05-.3-.2-.6-.5l-.7-.55c-.15-.1-.5-.45-1-1.1l-.1-.1c.2-.15.4-.35.6-.55l1.95-1.85 1.1-1c1-1 1.7-1.65 2.1-1.9l.5-.35c.4-.25.65-.45.75-.45.2-.15.45-.35.65-.6s.3-.5.3-.7l-.3-.65c-.55.2-1.2.65-2.05 1.35-.85.75-1.65 1.55-2.5 2.5-.8.9-1.6 1.65-2.4 2.3-.8.65-1.4.95-1.9 1-.15 0-1.5-1.05-4.1-3.2C3.1 2.6 1.45 1.2.7.55L.45.1c-.1.05-.2.15-.3.3C.05.55 0 .7 0 .85l.05.35.05.4 1.2 1 5.2 4.15c.1.05.3.2.6.5l.7.6c.15.1.5.45 1 1.1l.1.1c-.2.15-.4.35-.6.55l-1.95 1.85-1.1 1c-1 1-1.7 1.65-2.1 1.9l-.5.35c-.4.25-.65.45-.75.45-.25.15-.45.35-.65.6-.15.3-.25.55-.25.75l.3.65c.55-.2 1.2-.65 2.05-1.35.85-.75 1.65-1.55 2.5-2.5.8-.9 1.6-1.65 2.4-2.3.8-.65 1.4-.95 1.9-1 .15 0 1.5 1.05 4.1 3.2 2.6 2.15 4.3 3.55 5.05 4.2l.2.45c.1-.05.2-.15.3-.3.1-.15.15-.3.15-.45z">
                                            </path>
                                        </svg>
                                    </button>
                                </div>
                                <div className="edit-inner form-img-upload">
                                    <form onSubmit={addTagsData}>
                                        <div className="row">
                                            <div className="col-12"> 
                                                <div className="form-group">
                                                    <label htmlFor="name">Name</label>
                                                    <input type="text" id="name" name="name" value={tagsData.name} onChange={onChange} placeholder="Enter name" ref={nameRefFocus} />
                                                    <span className='text-danger pt-2'>{errors?.name}</span>
                                                </div>
                                                <div className="action-footer">
                                                                                                        <button type="submit" className="btn" disabled={showModel.title === 'Add' ? addTagsLoading.loading : updateTagsLoading.loading}>
                                                                                                                {(showModel.title === 'Add' ? addTagsLoading.loading : updateTagsLoading.loading) ? (
                                                                                                                        <div className="spinner-border text-light" style={{width: '16px', height: '16px'}} role="status">
                                                                                                                                <span className="visually-hidden">Loading...</span>
                                                                                                                        </div>
                                                                                                                ) : (
                                                                                                                        "Save"
                                                                                                                )}
                                                                                                        </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </Model> 
                        )
                    }
                </>
            {/* } */}
        </>
    );
};

export default Tags;
